import { sectionMigrationPageOpenedInReadOnlyMode } from '@wix/bi-logger-editor-data/v2';
import { panelOpened, clickOnPanel } from '@wix/bi-logger-editor/v2';
import {
  biLogger,
  fedopsLogger,
  sections as sectionsUtils,
} from '#packages/util';
import {
  resolveMigrationFlow,
  runPerPageMigration,
  resolveEmptyPageMigration,
} from './api';
import { MAX_MIGRATIONS_ATTEMPTS_PER_PAGE, MigrationOrigin } from './constants';
import { PageMigrationStatus, type PageMigrationResult } from './types';
import { pageFailedMigrationCounter } from './utils/pageMigrationCounter';
import {
  getIsImpersonated,
  hasPagesWithoutSectionsInRoot,
  perPageMigrationBanner,
  isPageToMigrateSections,
} from './utils';

import type { EditorAPI } from '#packages/editorAPI';
import type { SectionsMigrationScope as Scope } from './scope';
import { type PanelOptions } from '#packages/stateManagement';

const isMigrationRestricted = (scope: Scope) => {
  const { isInsideEditorX, isInsideAppStudio } = scope.editorParamsAPI;
  const isImpersonated = getIsImpersonated();

  const isMigrationRestricted =
    isInsideEditorX || isInsideAppStudio || isImpersonated;

  return isMigrationRestricted;
};

export const isSiteMigratedByRegularFlow = (scope: Scope): boolean => {
  const homePageId = scope.editorAPI.homePage.get();
  const homePageRef = scope.editorAPI.components.get.byId(homePageId);
  // Check home and master page data because for general flow we set isMigrated to master page but in per pages flow to master page and page
  const isHomePageDataEmpty = !hasPageSectionsData(scope.editorAPI, homePageId);
  const isMasterPageDataExists =
    hasPageSectionsData(
      scope.editorAPI,
      scope.editorAPI.pages.getMasterPageId(),
    ) || sectionsUtils.isSectionsEnabled();
  const homePageChildren = scope.editorAPI.components.getChildren(homePageRef);
  const homePageEmpty = homePageChildren.length === 0;
  const isHomePageHasSections =
    !homePageEmpty &&
    !hasPagesWithoutSectionsInRoot(scope.editorAPI.documentServices, [
      homePageRef,
    ]);

  return isHomePageDataEmpty && isMasterPageDataExists && isHomePageHasSections;
};

const hasPageSectionsData = (editorAPI: EditorAPI, pageId: string) =>
  sectionsUtils.getPageSectionsEditorData(editorAPI, pageId)?.isSectionsEnabled;

const showErrorModal = (scope: Scope, origin: MigrationOrigin) => {
  const { panelManager } = scope;
  const flow = resolveMigrationFlow(scope);

  const panelName = 'openPageErrorModal';

  const panelOptions: PanelOptions = {
    panelLoader: () =>
      import('./components/openPageErrorModal/openPageErrorModal').then(
        (m) => m.OpenPageErrorModal,
      ),
  };

  const panelProps = {
    beforeRefresh: () =>
      biLogger.report(
        clickOnPanel({
          panel_name: panelName,
          action_name: 'refresh',
          origin,
          flow,
        }),
      ),
    onCloseButtonClick: () =>
      biLogger.report(
        clickOnPanel({
          panel_name: panelName,
          action_name: 'X',
          origin,
          flow,
        }),
      ),
  };

  panelManager.openPanel(panelName, panelProps, panelOptions);

  biLogger.report(
    panelOpened({
      panel_name: panelName,
      origin,
    }),
  );
};

const reportReadonlyPageOpened = (scope: Scope, pageId: string) => {
  biLogger.report(
    sectionMigrationPageOpenedInReadOnlyMode({
      page_id: pageId,
      page_count: scope.editorAPI.pages.getPagesList()?.length,
      editor_working_mode: scope.editorAPI.isMobileEditor()
        ? 'mobile'
        : 'desktop',
    }),
  );
};

const runMigrationOnHomePage = async (scope: Scope) => {
  const { editorAPI, editorCoreAPI } = scope;
  const homePageId = editorAPI.pages.getCurrentPageId();

  if (isPageMigrated(scope, homePageId)) {
    editorCoreAPI.hooks.sectionsMigrationReady.resolve({
      isMigrated: false,
    });
    return {
      status: PageMigrationStatus.ALREADY_MIGRATED,
    };
  }

  if (resolveEmptyPageMigration(scope, MigrationOrigin.Init, homePageId)) {
    return {
      status: PageMigrationStatus.SUCCESS,
    };
  }

  let migrationResult;

  try {
    fedopsLogger.appLoadingPhaseStart('sections-migration-init');

    migrationResult = await runPerPageMigration(scope, homePageId, {
      origin: MigrationOrigin.Init,
    });

    fedopsLogger.appLoadingPhaseFinish('sections-migration-init');

    editorCoreAPI.hooks.sectionsMigrationReady.resolve({
      isMigrated: migrationResult.status === PageMigrationStatus.SUCCESS,
    });
  } catch (error: MaybeError) {
    editorCoreAPI.hooks.UNSTABLE_sectionsMigrationFailedOnHomePage.resolve();
  }

  return migrationResult;
};

const runMigrationOnPageNavigate = (scope: Scope) => {
  const pagesWithMigrationRunning = new Set<string>();
  scope.editorAPI.pages.hooks.beforePageNavigate.tap(
    async ({ pageId, origin }) => {
      perPageMigrationBanner.hide();
      scope.readonlyModeApi.disableReadonlyMode();

      if (pagesWithMigrationRunning.has(pageId) || !pageId) return;
      pagesWithMigrationRunning.add(pageId);

      if (resolveEmptyPageMigration(scope, origin, pageId)) {
        return;
      }

      if (!isPageToMigrateSections(scope.editorAPI.documentServices, pageId)) {
        return;
      }

      if (isPageMigrated(scope, pageId)) {
        return;
      }

      const navigationOrigin = origin ?? MigrationOrigin.PagesPanelNavigation;

      const failedMigrationCount =
        pageFailedMigrationCounter.getMigrationCount(pageId);

      if (failedMigrationCount === MAX_MIGRATIONS_ATTEMPTS_PER_PAGE) {
        perPageMigrationBanner.show();
        scope.readonlyModeApi.enableReadonlyMode();
        reportReadonlyPageOpened(scope, pageId);
        return;
      }

      try {
        const migrationFn = () =>
          runPerPageMigration(scope, pageId, { origin: navigationOrigin });
        await scope.migrationManagerApi.addMigrationToQueue(migrationFn);
        scope.editorAPI.history.clear();
      } catch (error: MaybeError) {
        if (!failedMigrationCount) {
          showErrorModal(scope, navigationOrigin);
        } else {
          perPageMigrationBanner.show();
          scope.readonlyModeApi.enableReadonlyMode();
          reportReadonlyPageOpened(scope, pageId);
        }

        pagesWithMigrationRunning.delete(pageId);
        pageFailedMigrationCounter.incrementMigrationCount(pageId);
      }
    },
  );
};

const runMigrationOnExitPreview = (scope: Scope) => {
  const { editorAPI } = scope;

  editorAPI.preview.registerToBeforeExitPreview(async () => {
    perPageMigrationBanner.hide();
    scope.readonlyModeApi.disableReadonlyMode();

    const currentPageId = editorAPI.pages.getCurrentPageId();

    if (
      resolveEmptyPageMigration(
        scope,
        MigrationOrigin.ExitPreview,
        currentPageId,
      )
    ) {
      return;
    }

    if (
      !isPageToMigrateSections(scope.editorAPI.documentServices, currentPageId)
    ) {
      return;
    }

    if (isPageMigrated(scope, currentPageId)) {
      return;
    }

    try {
      await runPerPageMigration(scope, currentPageId, {
        origin: MigrationOrigin.ExitPreview,
      });
      editorAPI.history.clear();
    } catch (error: MaybeError) {
      const homePageId = editorAPI.homePage.get();

      editorAPI.pages.navigateTo(homePageId);
    }
  });
};

const isPageMigrated = (scope: Scope, pageId: string) => {
  return hasPageSectionsData(scope.editorAPI, pageId);
};

export const isHomePageMigrated = (scope: Scope) => {
  const { editorAPI } = scope;
  const homePageId = editorAPI.pages.getCurrentPageId();
  return isPageMigrated(scope, homePageId);
};

export const initPerPageMigration = async (scope: Scope) => {
  if (isSiteMigratedByRegularFlow(scope) || isMigrationRestricted(scope)) {
    scope.editorCoreAPI.hooks.sectionsMigrationReady.resolve({
      isMigrated: false,
    });
    return;
  }

  const migrationResult: PageMigrationResult | undefined =
    await runMigrationOnHomePage(scope);

  if (migrationResult) {
    runMigrationOnPageNavigate(scope);

    runMigrationOnExitPreview(scope);
  }
};
